/**
 * Input validation for salary calculations.
 *
 * This module provides validation functions that ensure the calculation engine
 * receives valid inputs, failing fast with clear error messages rather than
 * silently producing NaN or incorrect results.
 */

/**
 * Custom error class for validation failures.
 * Allows calling code to distinguish validation errors from other errors.
 */
export class ValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ValidationError';
  }
}

/**
 * Validates user input before calculations.
 *
 * @param {Object} userInput - The user input object
 * @throws {ValidationError} If input is invalid
 */
export const validateUserInput = (userInput) => {
  // Check userInput exists
  if (userInput === null || userInput === undefined) {
    throw new ValidationError('userInput is required');
  }

  // Validate salary
  const { salary } = userInput;
  if (typeof salary !== 'number' || !Number.isFinite(salary)) {
    throw new ValidationError('salary must be a finite number');
  }
  if (salary < 0) {
    throw new ValidationError('salary must be non-negative');
  }

  // Validate overtime (optional field)
  const { overtime } = userInput;
  if (overtime && overtime.include) {
    const overtimeValue = Number(overtime.value);
    if (overtimeValue < 0) {
      throw new ValidationError('overtime value must be non-negative when included');
    }
  }

  // Validate pension (optional field)
  const { pension } = userInput;
  if (pension && pension.active) {
    const contribution = Number(pension.contribution);
    if (contribution < 0) {
      throw new ValidationError('pension contribution must be non-negative when active');
    }
  }
};

/**
 * Required properties for a valid config object.
 */
const REQUIRED_CONFIG_PROPERTIES = [
  'PERSONAL_ALLOWANCE',
  'PERSONAL_ALLOWANCE_THRESHOLD',
  'TAX_BANDS',
  'ADJUSTED_TAX_BANDS',
  'NI',
  'STUDENT_LOANS',
  'PENSIONS',
];

/**
 * Validates configuration object before calculations.
 *
 * @param {Object} config - The tax year configuration object
 * @throws {ValidationError} If config is invalid
 */
export const validateConfig = (config) => {
  // Check config exists
  if (config === null || config === undefined) {
    throw new ValidationError('config is required');
  }

  // Check all required properties exist
  for (const prop of REQUIRED_CONFIG_PROPERTIES) {
    if (config[prop] === undefined) {
      throw new ValidationError(`config is missing required property: ${prop}`);
    }
  }

  // Validate PENSIONS structure (removing reliance on fallback hack)
  if (!config.PENSIONS.QUALIFYING_EARNINGS) {
    throw new ValidationError(
      'config.PENSIONS must have QUALIFYING_EARNINGS property. ' +
      'Legacy PENSION.AUTO_ENROLMENT structure is no longer supported.'
    );
  }

  // Validate QUALIFYING_EARNINGS has required thresholds
  const qe = config.PENSIONS.QUALIFYING_EARNINGS;
  if (typeof qe.LOWER_THRESHOLD !== 'number' || typeof qe.UPPER_THRESHOLD !== 'number') {
    throw new ValidationError(
      'config.PENSIONS.QUALIFYING_EARNINGS must have LOWER_THRESHOLD and UPPER_THRESHOLD as numbers'
    );
  }
};
